import { useEffect, useRef, useState } from "react";
import { Trash2, Eraser, CheckCircle2, Circle, BellRing, Wallet, Loader2, X, Pencil, Check, Plus } from "lucide-react";
import { toast } from "sonner";
import { api, rp, errMsg } from "@/api";
import { ConfirmDialog, Modal } from "@/components/Dialogs";

const STATUS_TABS = [["all", "Semua"], ["belum_bayar", "Belum Bayar"], ["sebagian", "Sebagian"], ["lunas", "Lunas"]];
export const statusLabel = (st) => (st === "lunas" ? "Lunas" : st === "sebagian" ? "Sebagian" : "Belum Bayar");
export const statusClass = (st) => (st === "lunas" ? "lunas" : st === "sebagian" ? "sebagian" : "belum");

function PaymentModal({ order, q, onClose, onChanged }) {
  const [amount, setAmount] = useState(order ? String(order.remaining) : "");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [cur, setCur] = useState(order);
  const [saved, setSaved] = useState(false);
  const sending = useRef(false); // kunci sinkron: cegah klik ganda sebelum state busy ter-render
  if (!cur) return null;

  const add = async (e) => {
    e.preventDefault();
    if (sending.current) return;
    sending.current = true; setBusy(true);
    try {
      const { data } = await api.post(`/admin/orders/${cur.id}/payments`, { amount: Number(amount), note }, q);
      setCur(data); setAmount(""); setNote(""); setSaved(true);
      toast.success(data.status === "lunas" ? "Pesanan lunas" : `Pembayaran dicatat, sisa ${rp(data.remaining)}`);
      onChanged();
    } catch (err) { toast.error(errMsg(err)); } finally { sending.current = false; setBusy(false); }
  };
  const remove = async (pid) => {
    try {
      const { data } = await api.delete(`/admin/orders/${cur.id}/payments/${pid}`, q);
      setCur(data); setAmount(""); setSaved(true); toast.success("Pembayaran dihapus"); onChanged();
    } catch (err) { toast.error(errMsg(err)); }
  };

  return (
    <Modal open={!!order} onClose={onClose} title="Catat Pembayaran" testId="payment-modal">
      <div className="pay-summary" data-testid="payment-summary">
        <div><span className="stat-label">Total</span><div className="stat-value">{rp(cur.total)}</div></div>
        <div><span className="stat-label">Dibayar</span><div className="stat-value profit-text" data-testid="payment-paid">{rp(cur.paid_amount)}</div></div>
        <div><span className="stat-label">Sisa</span><div className="stat-value warn-text" data-testid="payment-remaining">{rp(cur.remaining)}</div></div>
      </div>
      {cur.payments.length > 0 && (
        <ul className="pay-list" data-testid="payment-list">
          {cur.payments.map((p) => (
            <li key={p.id} data-testid={`payment-${p.id}`}>
              <span>{new Date(p.paid_at).toLocaleString("id-ID")}{p.note ? ` · ${p.note}` : ""}{p.by ? ` · oleh ${p.by}` : ""}</span>
              <strong>{rp(p.amount)}</strong>
              <button className="icon-btn danger" onClick={() => remove(p.id)} title="Hapus pembayaran" data-testid={`payment-delete-${p.id}`}><X size={14} /></button>
            </li>
          ))}
        </ul>
      )}
      {cur.remaining > 0 && saved ? (
        <div className="actions-right">
          <button type="button" className="btn-ghost" onClick={onClose} data-testid="payment-close">Tutup</button>
          <button type="button" className="btn-primary" data-testid="payment-again" onClick={() => { if (window.confirm(`Pembayaran sudah tersimpan. Sisa tagihan ${rp(cur.remaining)}. Yakin MAU BAYAR LAGI?`)) { setAmount(""); setSaved(false); } }}>MAU BAYAR LAGI</button>
        </div>
      ) : cur.remaining > 0 ? (
        <form onSubmit={add}>
          <label className="field-label">Jumlah Bayar (Rp)</label>
          <input className="text-input" type="number" min="1" max={cur.remaining} value={amount} onChange={(e) => setAmount(e.target.value)} autoFocus data-testid="payment-amount" />
          <div className="chip-row" style={{ margin: "6px 0 10px" }}>
            {[0.25, 0.5].map((f) => <button type="button" key={f} className="chip" onClick={() => setAmount(String(Math.round(cur.remaining * f)))} data-testid={`payment-quick-${f}`}>{f * 100}%</button>)}
            <button type="button" className="chip" onClick={() => setAmount(String(cur.remaining))} data-testid="payment-quick-full">Lunasi ({rp(cur.remaining)})</button>
          </div>
          <label className="field-label">Catatan (opsional)</label>
          <input className="text-input" value={note} onChange={(e) => setNote(e.target.value)} placeholder="mis. transfer BCA / cash" data-testid="payment-note" />
          <div className="actions-right">
            <button type="button" className="btn-ghost" onClick={onClose} data-testid="payment-close">Tutup</button>
            <button type="submit" className="btn-primary" disabled={busy || !(Number(amount) > 0) || Number(amount) > cur.remaining} data-testid="payment-submit">{busy ? <Loader2 className="spin" size={16} /> : <Wallet size={16} />} Simpan Pembayaran</button>
          </div>
        </form>
      ) : (
        <div className="actions-right"><span className="status-pill lunas" data-testid="payment-lunas">Lunas</span><button className="btn-ghost" onClick={onClose} data-testid="payment-close">Tutup</button></div>
      )}
    </Modal>
  );
}

function EditOrderModal({ order, q, onClose, onChanged }) {
  const [vq, setVq] = useState(() => order.vouchers.map((v) => ({ ...v })));
  const [extras, setExtras] = useState(() => (order.extras || []).map((e) => ({ ...e })));
  const [name, setName] = useState(order.buyer_name || "");
  const [note, setNote] = useState(order.buyer_note || "");
  const [busy, setBusy] = useState(false);
  const [catalog, setCatalog] = useState([]);
  const [pick, setPick] = useState("");
  useEffect(() => { api.get("/admin/vouchers", q).then((r) => setCatalog(r.data || [])).catch(() => {}); }, []); // eslint-disable-line
  const setVQty = (i, val) => setVq((p) => p.map((v, idx) => (idx === i ? { ...v, qty: Math.max(0, val) } : v)));
  const removeV = (i) => setVq((p) => p.filter((_, idx) => idx !== i));
  const addVoucher = () => {
    const v = catalog.find((c) => c.id === pick);
    if (!v) return;
    setVq((p) => {
      const i = p.findIndex((x) => x.voucher_id === v.id);
      if (i >= 0) return p.map((x, idx) => (idx === i ? { ...x, qty: x.qty + 1 } : x));
      return [...p, { voucher_id: v.id, name: v.name, qty: 1, price: v.harga_beli, sell_price: v.harga_jual }];
    });
    setPick("");
  };
  const setExtra = (i, k, val) => setExtras((p) => p.map((e, idx) => (idx === i ? { ...e, [k]: val } : e)));
  const addExtra = () => setExtras((p) => [...p, { name: "", price: 0 }]);
  const removeExtra = (i) => setExtras((p) => p.filter((_, idx) => idx !== i));
  const vTotal = vq.reduce((s, v) => s + v.qty * v.price, 0);
  const eTotal = extras.reduce((s, e) => s + (e.name.trim() ? Number(e.price) || 0 : 0), 0);
  const newTotal = vTotal + eTotal;
  const paid = order.paid_amount || 0;
  const selisih = newTotal - paid;

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put(`/admin/orders/${order.id}`, {
        buyer_name: name, buyer_note: note,
        vouchers: vq.map((v) => ({ voucher_id: v.voucher_id, name: v.name, qty: v.qty })),
        extras: extras.filter((e) => e.name.trim()).map((e) => ({ name: e.name, price: Number(e.price) || 0 })),
      }, q);
      toast.success(`Pesanan diperbarui · Total ${rp(data.total)}`);
      onChanged(data); onClose();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  return (
    <Modal open={!!order} onClose={onClose} title="Edit Pesanan" testId="edit-order-modal">
      <label className="field-label">Nama Pembeli</label>
      <input className="text-input" value={name} onChange={(e) => setName(e.target.value)} data-testid="edit-order-name" />
      <label className="field-label" style={{ marginTop: 10 }}>Jumlah per Produk</label>
      {vq.length === 0 && <p className="muted-sm">Tidak ada baris voucher pada pesanan ini.</p>}
      {vq.map((v, i) => (
        <div key={v.voucher_id || v.name} className="edit-order-row" data-testid={`edit-order-voucher-${i}`}>
          <span className="edit-order-vname">{v.name} <span className="muted-sm">@ {rp(v.price)}</span></span>
          <div className="qty-ctl">
            <button type="button" onClick={() => setVQty(i, v.qty - 1)} data-testid={`edit-order-minus-${i}`}>−</button>
            <input type="number" min="0" value={v.qty} onChange={(e) => setVQty(i, parseInt(e.target.value || "0", 10))} data-testid={`edit-order-qty-${i}`} />
            <button type="button" onClick={() => setVQty(i, v.qty + 1)} data-testid={`edit-order-plus-${i}`}>+</button>
          </div>
          <strong className="edit-order-sub">{rp(v.qty * v.price)}</strong>
          <button type="button" className="icon-btn danger" onClick={() => removeV(i)} data-testid={`edit-order-vdel-${i}`}><X size={14} /></button>
        </div>
      ))}
      <div className="edit-order-row" data-testid="edit-order-add-voucher-row">
        <select className="text-input" value={pick} onChange={(e) => setPick(e.target.value)} data-testid="edit-order-catalog-select" style={{ flex: 1 }}>
          <option value="">+ Pilih Produk/Jasa dari katalog…</option>
          {catalog.map((c) => <option key={c.id} value={c.id}>{c.name} — {rp(c.harga_beli)}</option>)}
        </select>
        <button type="button" className="btn-ghost sm" onClick={addVoucher} disabled={!pick} data-testid="edit-order-add-voucher"><Plus size={13} /> Tambah</button>
      </div>
      <div className="panel-toolbar" style={{ margin: "12px 0 6px" }}>
        <label className="field-label" style={{ margin: 0 }}>Item Lain (barang/jasa)</label>
        <button type="button" className="btn-ghost sm" onClick={addExtra} data-testid="edit-order-add-extra"><Plus size={13} /> Tambah Item</button>
      </div>
      {extras.map((e, i) => (
        <div key={i} className="edit-order-row" data-testid={`edit-order-extra-${i}`}>
          <input className="text-input" placeholder="Nama item" value={e.name} onChange={(ev) => setExtra(i, "name", ev.target.value)} data-testid={`edit-order-extra-name-${i}`} />
          <input className="text-input" type="number" min="0" placeholder="Harga" value={e.price} onChange={(ev) => setExtra(i, "price", ev.target.value)} style={{ width: 110 }} data-testid={`edit-order-extra-price-${i}`} />
          <button type="button" className="icon-btn danger" onClick={() => removeExtra(i)} data-testid={`edit-order-extra-del-${i}`}><X size={14} /></button>
        </div>
      ))}
      <label className="field-label" style={{ marginTop: 10 }}>Catatan</label>
      <input className="text-input" value={note} onChange={(e) => setNote(e.target.value)} data-testid="edit-order-note" />
      <div className="pay-summary" style={{ marginTop: 12 }}>
        <div><span className="stat-label">Total Baru</span><div className="stat-value" data-testid="edit-order-total">{rp(newTotal)}</div></div>
        <div><span className="stat-label">Sudah Dibayar</span><div className="stat-value profit-text">{rp(paid)}</div></div>
        <div><span className="stat-label">{selisih >= 0 ? "Sisa Tagihan" : "Kelebihan Bayar"}</span><div className={`stat-value ${selisih >= 0 ? "warn-text" : "profit-text"}`} data-testid="edit-order-selisih">{rp(Math.abs(selisih))}</div></div>
      </div>
      {selisih < 0 && <p className="muted-sm" data-testid="edit-order-overpaid-note" style={{ color: "#c2410c" }}>Total baru lebih kecil dari yang sudah dibayar. Riwayat pembayaran tetap disimpan; kelebihan {rp(-selisih)} perlu diselesaikan sesuai kebijakan.</p>}
      <div className="actions-right" style={{ marginTop: 10 }}>
        <button type="button" className="btn-ghost" onClick={onClose} data-testid="edit-order-cancel">Batal</button>
        <button type="button" className="btn-primary" onClick={save} disabled={busy} data-testid="edit-order-save">{busy ? <Loader2 className="spin" size={16} /> : <Check size={16} />} Simpan Perubahan</button>
      </div>
    </Modal>
  );
}


export function reminderLink(o, store) {
  const items = [
    ...o.vouchers.map((v) => `- ${v.name} x${v.qty} = ${rp(v.qty * v.price)}`),
    ...(o.extras || []).map((e) => `- ${e.name} = ${rp(e.price)}`),
  ].join("\n");
  const tgl = new Date(o.created_at).toLocaleDateString("id-ID", { day: "numeric", month: "long", year: "numeric" });
  const wa = (store?.whatsapp_number || "").replace(/\D/g, "");
  const paidLine = o.paid_amount > 0 ? `\nSudah dibayar: ${rp(o.paid_amount)}` : "";
  const catLine = o.buyer_note ? `\nCatatan: ${o.buyer_note}` : "";
  const text = `Halo ${o.buyer_name || "Reseller"},\n\nIni pengingat tagihan dari *${store?.store_name || "toko"}* untuk pesanan tanggal ${tgl}:\n${items}\n\nTotal: ${rp(o.total)}${paidLine}${catLine}\n*Sisa tagihan: ${rp(o.remaining)}*\n\nMohon segera dilunasi.${wa ? ` Konfirmasi pembayaran ke WA ${wa}.` : ""} Terima kasih 🙏`;
  const phone = (o.reseller_phone || "").replace(/\D/g, "");
  return phone ? `https://wa.me/${phone}?text=${encodeURIComponent(text)}` : `https://wa.me/?text=${encodeURIComponent(text)}`;
}

export function OrderList({ storeId, range, onChanged, store }) {
  const [orders, setOrders] = useState(null);
  const [status, setStatus] = useState("all");
  const [resellerId, setResellerId] = useState("");
  const [resellers, setResellers] = useState([]);
  const [target, setTarget] = useState(null);
  const [payOrder, setPayOrder] = useState(null);
  const [editOrder, setEditOrder] = useState(null);

  const params = { ...(storeId ? { store_id: storeId } : {}), ...(range?.date_from ? { date_from: range.date_from, date_to: range.date_to } : {}), ...(status !== "all" ? { status } : {}), ...(resellerId ? { reseller_id: resellerId } : {}) };
  const q = { params };

  const load = () => api.get("/admin/orders", q).then((r) => setOrders(r.data));
  useEffect(() => { load(); }, [storeId, range?.date_from, range?.date_to, status, resellerId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    api.get("/admin/resellers", { params: storeId ? { store_id: storeId } : {} }).then((r) => setResellers(r.data || [])).catch(() => setResellers([]));
  }, [storeId]);

  const toggleStatus = async (o) => {
    const next = o.status === "lunas" ? "belum_bayar" : "lunas";
    try {
      await api.put(`/admin/orders/${o.id}/status`, { status: next }, { params: storeId ? { store_id: storeId } : {} });
      toast.success(next === "lunas" ? "Ditandai Lunas" : "Ditandai Belum Bayar");
      await load(); onChanged?.();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const doDelete = async () => {
    try {
      if (target === "all") { await api.delete("/admin/orders", { params: storeId ? { store_id: storeId } : {} }); toast.success("Semua pesanan dihapus"); }
      else { await api.delete(`/admin/orders/${target.id}`, { params: storeId ? { store_id: storeId } : {} }); toast.success("Pesanan dihapus"); }
      await load(); onChanged?.();
    } catch (e) { toast.error(errMsg(e)); }
  };

  if (!orders) return <p className="empty-note">Memuat...</p>;

  const rights = store?.access_rights || {};
  const canDelete = rights.hapus !== false;
  const canDeleteAll = rights.hapus_semua !== false;
  const canTagihWa = rights.edit !== false;
  const canEdit = rights.edit !== false;

  const unpaid = orders.filter((o) => o.remaining > 0);
  const unpaidTotal = unpaid.reduce((s, o) => s + o.remaining, 0);

  return (
    <div data-testid="order-panel">
      <div className="unpaid-bar" data-testid="unpaid-bar">
        <div>
          <span className="stat-label">Tagihan Belum Bayar ({range?.label || "Semua"})</span>
          <div className="stat-value" data-testid="unpaid-total">{rp(unpaidTotal)}</div>
        </div>
        <span className="muted-sm" data-testid="unpaid-count">{unpaid.length} pesanan belum lunas</span>
      </div>

      <div className="panel-toolbar">
        <div className="chip-row">
          {STATUS_TABS.map(([id, label]) => (
            <button key={id} className={`chip ${status === id ? "active" : ""}`} onClick={() => setStatus(id)} data-testid={`status-filter-${id}`}>{label}</button>
          ))}
          <select className="text-input" style={{ minHeight: 44, width: "auto", minWidth: 160, padding: "10px 12px", fontSize: 15 }} value={resellerId} onChange={(e) => setResellerId(e.target.value)} data-testid="reseller-filter">
            <option value="">Semua Reseller</option>
            {resellers.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
          </select>
        </div>
        <div className="toolbar-right">
          <span className="muted-sm" data-testid="orders-count">{orders.length} pesanan</span>
          {orders.length > 0 && (
            <button className="btn-ghost sm danger-text" onClick={() => setTarget("all")} disabled={!canDeleteAll} title={!canDeleteAll ? "Dinonaktifkan superadmin" : ""} data-testid="orders-clear-all"><Eraser size={14} /> Hapus Semua</button>
          )}
        </div>
      </div>

      {orders.length === 0 && <p className="empty-note" data-testid="orders-empty">Tidak ada pesanan pada filter ini.</p>}
      <div className="order-list" data-testid="order-list">
        {orders.map((o) => {
          const paid = o.status === "lunas";
          return (
            <div className={`order-card ${paid ? "paid" : "unpaid"}`} key={o.id} data-testid={`order-${o.id}`}>
              <div className="order-head">
                <div>
                  <strong>{o.buyer_name || "Tanpa nama"}</strong>
                  <span className={`status-pill ${statusClass(o.status)}`} data-testid={`order-status-${o.id}`}>{statusLabel(o.status)}</span>
                  {o.agen_id && <span className="status-pill assisted" data-testid={`order-agen-${o.id}`}>Agen {o.served_by_agen_name || o.agen_name} → Reseller {o.buyer_name}</span>}
                  {o.assisted_by && !o.agen_id && <span className="status-pill assisted" data-testid={`order-assisted-${o.id}`}>Dibantu admin ({o.assisted_by})</span>}
                  <div className="order-time">{new Date(o.created_at).toLocaleString("id-ID")}{paid && o.paid_at ? ` · lunas ${new Date(o.paid_at).toLocaleDateString("id-ID")}` : ""}</div>
                </div>
                <button className="icon-btn danger" onClick={() => setTarget(o)} disabled={!canDelete} title={!canDelete ? "Dinonaktifkan superadmin" : "Hapus pesanan"} data-testid={`order-delete-${o.id}`}><Trash2 size={16} /></button>
              </div>
              <ul className="order-items">
                {o.vouchers.map((v, i) => <li key={`v${i}`}>{v.name} × {v.qty} @ {rp(v.price)} — <strong>{rp(v.qty * v.price)}</strong></li>)}
                {(o.extras || []).map((e, i) => <li key={`e${i}`}>{e.name} — {rp(e.price)}</li>)}
              </ul>
              {o.komisi_agen > 0 && (
                <div className="muted-sm" style={{ marginTop: 4 }} data-testid={`order-agen-margin-${o.id}`}>
                  Komisi Agen{o.agen_name ? ` (${o.agen_name})` : ""}: <strong className="profit-text">{rp(o.komisi_agen)}</strong> · Setoran Pusat: <strong>{rp(o.setoran_pusat)}</strong>
                </div>
              )}
              {o.buyer_note && (
                <div className="order-note-box" style={{ background: "#fef3c7", padding: "4px 8px", borderRadius: 4, fontSize: 12, marginTop: 4, color: "#92400e" }} data-testid="order-catatan-display">
                  <strong>Catatan:</strong> {o.buyer_note}
                </div>
              )}
              {o.paid_amount > 0 && (
                <div className="pay-progress" data-testid={`order-pay-progress-${o.id}`}>
                  <div className="pay-bar"><div style={{ width: `${Math.min(100, (o.paid_amount / (o.total || 1)) * 100)}%` }} /></div>
                  <span className="muted-sm">Dibayar {rp(o.paid_amount)} · Sisa <strong className="warn-text" data-testid={`order-remaining-${o.id}`}>{rp(o.remaining)}</strong></span>
                </div>
              )}
              <div className="order-foot">
                <div className="pending-actions">
                  <button className={`status-btn ${paid ? "undo" : ""}`} onClick={() => toggleStatus(o)} data-testid={`order-toggle-status-${o.id}`}>
                    {paid ? <><Circle size={14} /> Batalkan Lunas</> : <><CheckCircle2 size={14} /> Tandai Lunas</>}
                  </button>
                  {!paid && (
                    <button className="btn-ghost sm" onClick={() => setPayOrder(o)} data-testid={`order-pay-${o.id}`}><Wallet size={13} /> Bayar Sebagian</button>
                  )}
                  <button className="btn-ghost sm" onClick={() => setEditOrder(o)} disabled={!canEdit} title={!canEdit ? "Dinonaktifkan superadmin" : "Edit pesanan"} data-testid={`order-edit-${o.id}`}><Pencil size={13} /> Edit</button>
                  {!paid && canTagihWa && (
                    <a className="wa-btn" href={reminderLink(o, store)} target="_blank" rel="noreferrer" title={o.reseller_phone ? `Kirim ke ${o.reseller_phone}` : "Nomor WA reseller tidak ada — pilih kontak manual"} data-testid={`order-remind-${o.id}`}>
                      <BellRing size={13} /> Ingatkan WA
                    </a>
                  )}
                  {!paid && !canTagihWa && (
                    <span className="wa-btn btn-off" title="Dinonaktifkan superadmin" data-testid={`order-remind-${o.id}`}>
                      <BellRing size={13} /> Ingatkan WA
                    </span>
                  )}
                </div>
                <span className="order-total">Total Bayar: <strong>{rp(o.total)}</strong></span>
              </div>
            </div>
          );
        })}
      </div>

      {payOrder && <PaymentModal key={payOrder.id} order={payOrder} q={{ params: storeId ? { store_id: storeId } : {} }} onClose={() => { setPayOrder(null); load(); }} onChanged={() => onChanged?.()} />}
      {editOrder && <EditOrderModal key={`e${editOrder.id}`} order={editOrder} q={{ params: storeId ? { store_id: storeId } : {} }} onClose={() => setEditOrder(null)} onChanged={() => { load(); onChanged?.(); }} />}
      <ConfirmDialog
        open={!!target}
        onClose={() => setTarget(null)}
        title={target === "all" ? "Hapus Semua Pesanan" : "Hapus Pesanan"}
        message={target === "all"
          ? "Semua pesanan di toko ini (termasuk di luar filter) akan dihapus permanen. Lanjutkan?"
          : `Hapus pesanan dari "${target?.buyer_name || "Tanpa nama"}" senilai ${rp(target?.total)}?`}
        confirmLabel={target === "all" ? "Hapus Semua" : "Hapus"}
        onConfirm={doDelete}
        testId="order-confirm"
      />
    </div>
  );
}
